// The console: what the program itself said, and nothing else.
//
// `print` lines and `assert` verdicts are in the trace already, but the trace is
// a swimlane diagram of every read, write, commit and abort the run made — a
// handful of output rows scattered through hundreds of machinery ones. This
// panel is the same events with the machinery left out: one line each, in the
// order they happened, tagged with the virtual time and the thread that produced
// them (output from concurrent threads interleaves, and which thread said what
// is usually the point).
//
// Assertions read the way they do in the trace: `✓` in green when the claim
// held, `✗` in red when it did not. They belong here because they are the
// program talking about itself — a run's output is what it printed *and* what it
// claimed — and because a failed one is the thing you most want to not have to
// go hunting for.
//
// It stays a *view of the trace* rather than a second recording: each line knows
// its trace row, so clicking one selects that row — the trace scrolls to it, the
// debugger opens on the acting thread's stack, and the editor highlights the
// statement that wrote it.

import { useEffect, useMemo, useRef } from "react";
import type { RunResult } from "../../src/backend/interp/interpreter.js";
import type { TraceEvent } from "../../src/backend/interp/trace.js";

/** One line of output, and the trace row it came from. `passed` is the
 *  assertion's verdict, and absent on a print. */
export interface ConsoleLine {
  row: number;
  time: number;
  thread: number;
  message: string;
  passed?: boolean;
}

/** The run's output: its `print` and `assert` events, in trace order, each
 *  remembering the index of the trace row it is. */
function consoleLines(events: readonly TraceEvent[]): ConsoleLine[] {
  const out: ConsoleLine[] = [];
  events.forEach((ev, row) => {
    if (ev.type === "print") out.push({ row, time: ev.time, thread: ev.thread, message: ev.message });
    else if (ev.type === "assert")
      out.push({ row, time: ev.time, thread: ev.thread, message: ev.message, passed: ev.passed });
  });
  return out;
}

/** An assertion's text: its own message, or a stand-in for the `assert` that
 *  was written without one — a bare verdict with no line to read is worse than
 *  a dull one. */
function assertText(l: ConsoleLine): string {
  const mark = l.passed ? "✓" : "✗";
  return `${mark} ${l.message || (l.passed ? "assertion held" : "assertion failed")}`;
}

interface ConsolePanelProps {
  result: RunResult | null;
  selectedRow: number;
  onSelectRow(row: number): void;
}

export function ConsolePanel({ result, selectedRow, onSelectRow }: ConsolePanelProps) {
  const lines = useMemo(() => (result ? consoleLines(result.trace.events) : []), [result]);
  const listRef = useRef<HTMLDivElement>(null);

  // Selection travels both ways: picking one of these rows anywhere else — the
  // trace, the happens-before graph, a node of the timeline tree — scrolls this
  // list to its line, so the console shows the same moment as everything else.
  useEffect(() => {
    const el = listRef.current?.querySelector(".cline.sel");
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedRow, lines]);

  if (!result) return <div id="console" className="console-empty">run the program to see its output</div>;
  if (lines.length === 0)
    return <div id="console" className="console-empty">this run printed nothing and asserted nothing</div>;

  return (
    <div id="console" ref={listRef}>
      {lines.map((l) => (
        <div
          key={l.row}
          className={`cline${l.row === selectedRow ? " sel" : ""}`}
          onClick={() => onSelectRow(l.row)}
          title="show this line in the trace"
        >
          <span className="ctime">t={l.time.toFixed(2)}</span>
          <span className="cthread">T{l.thread}</span>
          {l.passed === undefined ? (
            <span className="cmsg">{l.message}</span>
          ) : (
            <span className={`cmsg cassert ${l.passed ? "pass" : "fail"}`}>{assertText(l)}</span>
          )}
        </div>
      ))}
    </div>
  );
}
